import { api, toQuery } from './client';
import type {
  Amenity,
  AuthResponse,
  Business,
  BusinessWriteBody,
  CheckoutConfirmation,
  DeliveryStatus,
  Enquiry,
  Favourite,
  ListingPlan,
  Locality,
  Member,
  Notification,
  OwnerDashboard,
  OwnerPropertyListItem,
  OrganisationDashboard,
  MultiBranchStatus,
  BranchEmployee,
  BranchEmployeeWriteBody,
  PagedResult,
  PartnerAccountType,
  Payment,
  PaymentOrder,
  PaymentRequirement,
  Photo,
  PropertyDetail,
  PropertySearchResult,
  PropertySummary,
  Room,
  RoomType,
  Subscription,
  TenantPreference,
  UserProfile,
} from './types';

// =====================================================================================
// Auth
// =====================================================================================

export const authApi = {
  register: (body: {
    fullName: string;
    email: string;
    phone: string;
    password: string;
    accountType: 'Customer' | 'Owner';
    businessName?: string;
    categories?: string[];
  }) => api.post<AuthResponse>('/api/auth/register', body).then((r) => r.data),

  /**
   * Signs in either partner account type.
   *
   * An owner is identified by email or phone, which is unique platform-wide; the
   * organisation name is optional and, when given, is checked. A branch employee is
   * identified by a username that is unique only inside their organisation, so for them
   * the organisation name is part of the credential and is required.
   */
  login: (
    emailOrPhone: string,
    password: string,
    options: { organizationName?: string; accountType?: PartnerAccountType } = {},
  ) =>
    api
      .post<AuthResponse>('/api/auth/login', {
        emailOrPhone,
        password,
        organizationName: options.organizationName?.trim() || undefined,
        accountType: options.accountType ?? 'Owner',
      })
      .then((r) => r.data),

  logout: (refreshToken: string, allSessions = false) =>
    api.post(`/api/auth/logout${toQuery({ allSessions })}`, { refreshToken }),

  me: () => api.get<UserProfile>('/api/auth/me').then((r) => r.data),

  forgotPassword: (emailOrPhone: string) =>
    api.post<DeliveryStatus>('/api/auth/forgot-password', { emailOrPhone }).then((r) => r.data),

  resetPassword: (body: { email: string; token: string; newPassword: string }) =>
    api.post('/api/auth/reset-password', body),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/api/auth/change-password', { currentPassword, newPassword }),

  sendVerification: (channel: 'email' | 'phone') =>
    api.post<DeliveryStatus>('/api/auth/send-verification', { channel }).then((r) => r.data),

  confirmVerification: (channel: 'email' | 'phone', code: string) =>
    api.post('/api/auth/confirm-verification', { channel, code }),
};

// =====================================================================================
// Public discovery
// =====================================================================================

export interface BaseSearchParams {
  locality?: string;
  city?: string;
  keyword?: string;
  minPrice?: number;
  maxPrice?: number;
  latitude?: number;
  longitude?: number;
  radiusKm?: number;
  amenities?: string[];
  sort?: string;
  page?: number;
  pageSize?: number;
}

export interface PgSearchParams extends BaseSearchParams {
  gender?: string;
  sharingTypes?: string[];
  foodProvided?: boolean;
  airConditioned?: boolean;
  attachedBathroom?: boolean;
  minAvailableBeds?: number;
}

export interface HomeSearchParams extends BaseSearchParams {
  bedrooms?: number[];
  homeTypes?: string[];
  furnishing?: string[];
  tenantPreferences?: string[];
  hasCarParking?: boolean;
  availableBy?: string;
}

export interface HotelSearchParams extends BaseSearchParams {
  hotelTypes?: string[];
  roomCategories?: string[];
  guests?: number;
  airConditioned?: boolean;
  minStarRating?: number;
  checkIn?: string;
  checkOut?: string;
}

export const searchApi = {
  pg: (params: PgSearchParams) =>
    api.get<PropertySearchResult>(`/api/search/pg${toQuery(params as Record<string, unknown>)}`).then((r) => r.data),

  homes: (params: HomeSearchParams) =>
    api.get<PropertySearchResult>(`/api/search/homes${toQuery(params as Record<string, unknown>)}`).then((r) => r.data),

  hotels: (params: HotelSearchParams) =>
    api.get<PropertySearchResult>(`/api/search/hotels${toQuery(params as Record<string, unknown>)}`).then((r) => r.data),
};

export const propertyApi = {
  get: (idOrSlug: string) => api.get<PropertyDetail>(`/api/properties/${idOrSlug}`).then((r) => r.data),

  toggleFavourite: (propertyId: string) =>
    api.post<boolean>(`/api/properties/${propertyId}/favourite`).then((r) => r.data),

  report: (propertyId: string, body: { reason: string; details?: string; contactDetail?: string }) =>
    api.post(`/api/properties/${propertyId}/report`, { propertyId, ...body }),
};

export const referenceApi = {
  amenities: (category?: string, filterableOnly = false) =>
    api.get<Amenity[]>(`/api/reference/amenities${toQuery({ category, filterableOnly })}`).then((r) => r.data),

  tenantPreferences: () => api.get<TenantPreference[]>('/api/reference/tenant-preferences').then((r) => r.data),

  localities: (city?: string, category?: string) =>
    api.get<Locality[]>(`/api/reference/localities${toQuery({ city, category })}`).then((r) => r.data),

  settings: () => api.get<Record<string, string>>('/api/reference/settings').then((r) => r.data),
};

export const enquiryApi = {
  create: (body: {
    propertyId: string;
    roomTypeId?: string;
    name: string;
    phone: string;
    email?: string;
    message?: string;
    checkInDate?: string;
    checkOutDate?: string;
    adults?: number;
    children?: number;
    roomsRequired?: number;
    moveInDate?: string;
  }) => api.post<Enquiry>('/api/enquiries', body).then((r) => r.data),
};

// =====================================================================================
// Customer area
// =====================================================================================

export const meApi = {
  favourites: (page = 1, pageSize = 20) =>
    api.get<PagedResult<Favourite>>(`/api/me/favourites${toQuery({ page, pageSize })}`).then((r) => r.data),

  favouriteIds: () => api.get<string[]>('/api/me/favourites/ids').then((r) => r.data),

  recentlyViewed: (take = 8) =>
    api.get<PropertySummary[]>(`/api/me/recently-viewed${toQuery({ take })}`).then((r) => r.data),

  enquiries: (page = 1, pageSize = 20) =>
    api.get<PagedResult<Enquiry>>(`/api/me/enquiries${toQuery({ page, pageSize })}`).then((r) => r.data),

  notifications: (unreadOnly = false, page = 1, pageSize = 20) =>
    api
      .get<PagedResult<Notification>>(`/api/me/notifications${toQuery({ unreadOnly, page, pageSize })}`)
      .then((r) => r.data),

  markNotificationsRead: (ids?: string[]) => api.post('/api/me/notifications/read', ids ?? null),
};

// =====================================================================================
// Owner portal
// =====================================================================================

/**
 * Organisation-level operations.
 *
 * Every route is scoped by the organisation id, and the server re-checks that the caller
 * may act on it. Nothing here decides permission or eligibility - it carries the server's
 * answer back, including the wording shown to the owner.
 */
export const organisationApi = {
  dashboard: (businessId: string) =>
    api
      .get<OrganisationDashboard>(`/api/owner/organisations/${businessId}/dashboard`)
      .then((r) => r.data),

  multiBranch: (businessId: string) =>
    api
      .get<MultiBranchStatus>(`/api/owner/organisations/${businessId}/multi-branch`)
      .then((r) => r.data),

  /** Only succeeds while the Super Admin has payment switched off for the entitlement. */
  enableMultiBranch: (businessId: string) =>
    api
      .post<MultiBranchStatus>(`/api/owner/organisations/${businessId}/multi-branch/enable`)
      .then((r) => r.data),

  /**
   * Starts payment for the yearly multi-branch entitlement. The amount is set by the
   * server from the platform configuration; nothing about the price is sent from here.
   * Creating an order grants nothing on its own.
   */
  createMultiBranchOrder: (businessId: string) =>
    api
      .post<PaymentOrder>(`/api/owner/organisations/${businessId}/multi-branch/order`)
      .then((r) => r.data),

  createBranch: (
    businessId: string,
    // Same shape the listing form posts. Typed as unknown here to match
    // ownerPropertyApi.create rather than introduce a second definition of it.
    branch: unknown,
    employee?: BranchEmployeeWriteBody,
  ) =>
    api
      .post<PropertyDetail>(`/api/owner/organisations/${businessId}/branches`, { branch, employee })
      .then((r) => r.data),

  employees: (businessId: string) =>
    api
      .get<BranchEmployee[]>(`/api/owner/organisations/${businessId}/employees`)
      .then((r) => r.data),

  createEmployee: (businessId: string, branchId: string, body: BranchEmployeeWriteBody) =>
    api
      .post<BranchEmployee>(
        `/api/owner/organisations/${businessId}/branches/${branchId}/employees`,
        body,
      )
      .then((r) => r.data),

  assignEmployee: (businessId: string, branchId: string, membershipId: string) =>
    api
      .post<BranchEmployee>(
        `/api/owner/organisations/${businessId}/branches/${branchId}/employees/assign`,
        { membershipId },
      )
      .then((r) => r.data),

  removeEmployeeAssignment: (businessId: string, membershipId: string, branchId: string) =>
    api.delete(
      `/api/owner/organisations/${businessId}/employees/${membershipId}/branches/${branchId}`,
    ),

  changeUsername: (businessId: string, membershipId: string, username: string) =>
    api.put(`/api/owner/organisations/${businessId}/employees/${membershipId}/username`, { username }),

  /** The owner does not supply the old password; the change ends the employee's sessions. */
  resetPassword: (businessId: string, membershipId: string, newPassword: string) =>
    api.put(`/api/owner/organisations/${businessId}/employees/${membershipId}/password`, { newPassword }),

  setActive: (businessId: string, membershipId: string, isActive: boolean, reason?: string) =>
    api.put(`/api/owner/organisations/${businessId}/employees/${membershipId}/active`, { isActive, reason }),
};

export const businessApi = {
  list: () => api.get<Business[]>('/api/owner/businesses').then((r) => r.data),

  get: (id: string) => api.get<Business>(`/api/owner/businesses/${id}`).then((r) => r.data),

  create: (body: BusinessWriteBody) => api.post<Business>('/api/owner/businesses', body).then((r) => r.data),

  update: (id: string, body: BusinessWriteBody) =>
    api.put<Business>(`/api/owner/businesses/${id}`, body).then((r) => r.data),

  addCategory: (id: string, category: string) =>
    api.post<Business>(`/api/owner/businesses/${id}/categories`, { category }).then((r) => r.data),

  archive: (id: string) => api.delete(`/api/owner/businesses/${id}`),

  members: (id: string) => api.get<Member[]>(`/api/owner/businesses/${id}/members`).then((r) => r.data),

  addMember: (
    id: string,
    body: { email: string; canManageBilling: boolean; canPublishListings: boolean; canManageProperties: boolean },
  ) => api.post<Member>(`/api/owner/businesses/${id}/members`, body).then((r) => r.data),

  removeMember: (businessId: string, membershipId: string) =>
    api.delete(`/api/owner/businesses/${businessId}/members/${membershipId}`),
};

export const ownerPropertyApi = {
  list: (params: { businessId?: string; category?: string; status?: string; keyword?: string; page?: number; pageSize?: number }) =>
    api
      .get<PagedResult<OwnerPropertyListItem>>(`/api/owner/properties${toQuery(params as Record<string, unknown>)}`)
      .then((r) => r.data),

  get: (id: string) => api.get<PropertyDetail>(`/api/owner/properties/${id}`).then((r) => r.data),

  create: (body: unknown) => api.post<PropertyDetail>('/api/owner/properties', body).then((r) => r.data),

  update: (id: string, body: unknown) => api.put<PropertyDetail>(`/api/owner/properties/${id}`, body).then((r) => r.data),

  submit: (id: string) => api.post<PropertyDetail>(`/api/owner/properties/${id}/submit`).then((r) => r.data),

  archive: (id: string) => api.delete(`/api/owner/properties/${id}`),

  deletePermanently: (id: string) => api.delete(`/api/owner/properties/${id}/permanent`),

  restore: (id: string) => api.post(`/api/owner/properties/${id}/restore`),

  roomTypes: (propertyId: string) =>
    api.get<RoomType[]>(`/api/owner/properties/${propertyId}/room-types`).then((r) => r.data),

  createRoomType: (propertyId: string, body: unknown) =>
    api.post<RoomType>(`/api/owner/properties/${propertyId}/room-types`, body).then((r) => r.data),

  updateRoomType: (roomTypeId: string, body: unknown) =>
    api.put<RoomType>(`/api/owner/properties/room-types/${roomTypeId}`, body).then((r) => r.data),

  archiveRoomType: (roomTypeId: string) => api.delete(`/api/owner/properties/room-types/${roomTypeId}`),

  rooms: (roomTypeId: string) =>
    api.get<Room[]>(`/api/owner/properties/room-types/${roomTypeId}/rooms`).then((r) => r.data),

  createRoom: (roomTypeId: string, body: { roomNumber: string; floorNumber?: number; bedLabels: string[] }) =>
    api.post<Room>(`/api/owner/properties/room-types/${roomTypeId}/rooms`, body).then((r) => r.data),

  archiveRoom: (roomId: string) => api.delete(`/api/owner/properties/rooms/${roomId}`),

  updateBedStatus: (bedId: string, status: string, notes?: string) =>
    api.put('/api/owner/properties/beds/status', { bedId, status, notes }),

  updateAvailability: (propertyId: string, body: { roomTypeId?: string; availableUnits?: number; totalUnits?: number; notes?: string }) =>
    api.post<RoomType[]>(`/api/owner/properties/${propertyId}/availability`, body).then((r) => r.data),

  photos: (propertyId: string) =>
    api.get<Photo[]>(`/api/owner/properties/${propertyId}/photos`).then((r) => r.data),

  uploadPhoto: (propertyId: string, file: File, category: string, roomTypeId?: string) => {
    const form = new FormData();
    form.append('file', file);
    form.append('category', category);
    if (roomTypeId) form.append('roomTypeId', roomTypeId);

    return api
      .post<Photo>(`/api/owner/properties/${propertyId}/photos`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  updatePhoto: (photoId: string, body: { category?: string; caption?: string; altText?: string; isPrimary?: boolean }) =>
    api.put<Photo>(`/api/owner/properties/photos/${photoId}`, body).then((r) => r.data),

  reorderPhotos: (propertyId: string, photoIdsInOrder: string[]) =>
    api.put(`/api/owner/properties/${propertyId}/photos/order`, { photoIdsInOrder }),

  deletePhoto: (photoId: string) => api.delete(`/api/owner/properties/photos/${photoId}`),
};

export const ownerEnquiryApi = {
  list: (params: { propertyId?: string; businessId?: string; status?: string; page?: number; pageSize?: number }) =>
    api.get<PagedResult<Enquiry>>(`/api/owner/enquiries${toQuery(params as Record<string, unknown>)}`).then((r) => r.data),

  update: (id: string, status: string, ownerNotes?: string) =>
    api.put<Enquiry>(`/api/owner/enquiries/${id}`, { status, ownerNotes }).then((r) => r.data),
};

export const billingApi = {
  /**
   * Whether a category charges at all, what it charges, and whether it can be paid yet.
   * Always read this before showing an owner anything about money.
   */
  requirement: (category: string) =>
    api.get<PaymentRequirement>(`/api/owner/billing/requirement${toQuery({ category })}`).then((r) => r.data),

  plans: (category?: string, billingType?: string) =>
    api.get<ListingPlan[]>(`/api/owner/billing/plans${toQuery({ category, billingType })}`).then((r) => r.data),

  createOrder: (propertyId: string, planId: string, idempotencyKey?: string) =>
    api.post<PaymentOrder>('/api/owner/billing/orders', { propertyId, planId, idempotencyKey }).then((r) => r.data),

  confirmCheckout: (body: {
    paymentId: string;
    providerOrderId: string;
    providerPaymentId: string;
    signature: string;
  }) => api.post<CheckoutConfirmation>('/api/owner/billing/orders/confirm', body).then((r) => r.data),

  payments: (params: { businessId?: string; propertyId?: string; status?: string; page?: number; pageSize?: number }) =>
    api.get<PagedResult<Payment>>(`/api/owner/billing/payments${toQuery(params as Record<string, unknown>)}`).then((r) => r.data),

  subscriptions: (businessId?: string) =>
    api.get<Subscription[]>(`/api/owner/billing/subscriptions${toQuery({ businessId })}`).then((r) => r.data),

  /** Development only; the API refuses this outside a development environment. */
  simulatePayment: (paymentId: string, succeed = true) =>
    api.post<Payment>('/api/owner/billing/payments/simulate', { paymentId, succeed }).then((r) => r.data),
};

/**
 * The owner dashboard is assembled on the client from the listing, enquiry and
 * subscription endpoints, so there is no extra server round trip to maintain for it.
 */
export async function loadOwnerDashboard(): Promise<OwnerDashboard> {
  const [businesses, listings, enquiries, subscriptions] = await Promise.all([
    businessApi.list(),
    ownerPropertyApi.list({ pageSize: 50 }),
    ownerEnquiryApi.list({ pageSize: 5 }),
    billingApi.subscriptions(),
  ]);

  const items = listings.items;
  const byStatus = (status: string) => items.filter((i) => i.status === status).length;

  const pgItems = items.filter((i) => i.category === 'PgHostel');
  const hotelItems = items.filter((i) => i.category === 'Hotel');
  const homeItems = items.filter((i) => i.category === 'HomeRental');

  const availableBeds = pgItems
    .filter((i) => i.availabilityConfidence !== 'Unconfirmed')
    .reduce((total, i) => total + (i.availableUnits ?? 0), 0);
  const liveSubscriptions = subscriptions.filter(
    (subscription) =>
      subscription.propertyId !== undefined &&
      (subscription.status === 'Active' || subscription.status === 'ExpiringSoon'),
  );

  return {
    businessCount: businesses.length,
    branchCount: pgItems.length + hotelItems.length,
    publishedCount: byStatus('Active') + byStatus('ExpiringSoon'),
    draftCount: byStatus('Draft'),
    pendingPaymentCount: byStatus('PendingPayment'),
    pendingReviewCount: byStatus('PendingReview'),
    expiredCount: byStatus('Expired'),
    suspendedCount: byStatus('Suspended'),
    availableBeds: pgItems.length > 0 ? availableBeds : undefined,
    pgBranchCount: pgItems.length > 0 ? pgItems.length : undefined,
    homeListingCount: homeItems.length > 0 ? homeItems.length : undefined,
    hotelBranchCount: hotelItems.length > 0 ? hotelItems.length : undefined,
    hotelRoomCount: hotelItems.length > 0 ? hotelItems.reduce((t, i) => t + i.roomTypeCount, 0) : undefined,
    newEnquiryCount: items.reduce((total, i) => total + i.newEnquiryCount, 0),
    totalEnquiryCount: enquiries.totalCount,
    viewCountLast30Days: items.reduce((total, i) => total + i.viewCount, 0),
    activeSubscriptionCount: liveSubscriptions.length,
    expiringSoonCount: liveSubscriptions.filter((s) => s.status === 'ExpiringSoon').length,
    pendingPaymentTotalCount: items.filter((i) => i.requiresPayment).length,
    staleAvailabilityCount: items.filter(
      (i) => i.category !== 'HomeRental' && i.availabilityConfidence === 'Unconfirmed',
    ).length,
    recentEnquiries: enquiries.items,
    upcomingRenewals: liveSubscriptions
      .filter((s) => s.daysRemaining !== undefined && s.daysRemaining <= 14)
      .map((s) => ({
        id: s.id,
        planCode: s.planCode,
        planName: s.planName,
        status: s.status,
        startUtc: s.startUtc,
        expiryUtc: s.expiryUtc,
        daysRemaining: s.daysRemaining,
        totalAmount: s.totalAmount,
        currency: s.currency,
        autoRenew: s.autoRenew,
      })),
    currency: 'INR',
  };
}
